import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiStar, FiUser, FiCalendar, FiMessageSquare, FiTrendingUp, FiFilter } = FiIcons;

const CandidateEvaluations = () => {
  // Mock data - in real app, this would come from the DataContext or API
  const [evaluations] = useState([
    {
      id: '1',
      candidateId: '123',
      candidateName: 'John Doe',
      companyName: 'TechCorp Inc.',
      ratings: {
        englishLevel: 4,
        appearance: 5,
        technicalSkills: 4,
        communicationSkills: 5
      },
      notes: 'Excellent candidate with strong technical skills and great communication. Very professional appearance.',
      timestamp: '2024-03-15T10:30:00Z',
      interviewPosition: 'Frontend Developer'
    },
    {
      id: '2',
      candidateId: '124',
      candidateName: 'Jane Smith',
      companyName: 'DataSoft Solutions',
      ratings: {
        englishLevel: 3,
        appearance: 4,
        technicalSkills: 5,
        communicationSkills: 4
      },
      notes: 'Strong technical background, good problem-solving skills. English could use some improvement.',
      timestamp: '2024-03-15T11:15:00Z',
      interviewPosition: 'Data Scientist'
    },
    {
      id: '3',
      candidateId: '125',
      candidateName: 'Mike Johnson',
      companyName: 'TechCorp Inc.',
      ratings: {
        englishLevel: 5,
        appearance: 4,
        technicalSkills: 3,
        communicationSkills: 5
      },
      notes: 'Great communicator and very professional. Technical skills need some development.',
      timestamp: '2024-03-15T14:20:00Z',
      interviewPosition: 'Backend Developer'
    }
  ]);

  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');

  const renderStars = (rating) => {
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <SafeIcon
            key={star}
            icon={FiStar}
            className={star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  const getOverallRating = (ratings) => {
    const values = Object.values(ratings);
    return (values.reduce((sum, rating) => sum + rating, 0) / values.length).toFixed(1);
  };

  const filteredEvaluations = evaluations.filter(evaluation => {
    if (filter === 'all') return true;
    const overallRating = getOverallRating(evaluation.ratings);
    if (filter === 'high') return overallRating >= 4;
    if (filter === 'medium') return overallRating >= 3 && overallRating < 4;
    if (filter === 'low') return overallRating < 3;
    return true;
  });

  const sortedEvaluations = filteredEvaluations.sort((a, b) => {
    if (sortBy === 'recent') return new Date(b.timestamp) - new Date(a.timestamp);
    if (sortBy === 'rating-high') {
      return getOverallRating(b.ratings) - getOverallRating(a.ratings);
    }
    if (sortBy === 'rating-low') {
      return getOverallRating(a.ratings) - getOverallRating(b.ratings);
    }
    return 0;
  });

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-gray-900">Candidate Evaluations</h3>
          <div className="flex items-center space-x-4">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Ratings</option>
              <option value="high">High (4-5 stars)</option>
              <option value="medium">Medium (3 stars)</option>
              <option value="low">Low (1-2 stars)</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="recent">Most Recent</option>
              <option value="rating-high">Highest Rated</option>
              <option value="rating-low">Lowest Rated</option>
            </select>
          </div>
        </div>
      </div>

      {/* Evaluations List */}
      <div className="p-6">
        {sortedEvaluations.length === 0 ? (
          <div className="text-center py-12">
            <SafeIcon icon={FiUser} className="text-gray-400 text-4xl mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">No evaluations yet</h4>
            <p className="text-gray-600">Start by scanning candidate QR codes and rating them.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {sortedEvaluations.map((evaluation, index) => (
              <motion.div
                key={evaluation.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiUser} className="text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{evaluation.candidateName}</h4>
                      <p className="text-sm text-gray-600">{evaluation.interviewPosition}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        <SafeIcon icon={FiCalendar} className="inline mr-1" />
                        {new Date(evaluation.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">Overall:</span>
                      <span className="text-lg font-semibold text-blue-600">
                        {getOverallRating(evaluation.ratings)}/5
                      </span>
                    </div>
                    {renderStars(Math.round(getOverallRating(evaluation.ratings)))}
                  </div>
                </div>

                {/* Ratings Breakdown */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">English</p>
                    {renderStars(evaluation.ratings.englishLevel)}
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">Appearance</p>
                    {renderStars(evaluation.ratings.appearance)}
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">Technical</p>
                    {renderStars(evaluation.ratings.technicalSkills)}
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">Communication</p>
                    {renderStars(evaluation.ratings.communicationSkills)}
                  </div>
                </div>

                {/* Notes */}
                {evaluation.notes && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <SafeIcon icon={FiMessageSquare} className="text-blue-600 mt-1" />
                      <div>
                        <p className="text-sm font-medium text-blue-900 mb-1">Notes:</p>
                        <p className="text-sm text-blue-800">{evaluation.notes}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-500">
                    Evaluated by: <span className="font-medium">{evaluation.companyName}</span>
                  </p>
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <SafeIcon icon={FiTrendingUp} />
                    <span>Rating Completed</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateEvaluations;